import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Lightbulb, ArrowUp } from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

/**
 * OnboardingTour Component - แนะนำการใช้งานแบบ step-by-step
 */
const OnboardingTour = () => {
  const { 
    isOnboardingActive, 
    currentStep, 
    nextStep, 
    previousStep, 
    skipOnboarding, 
    completeOnboarding 
  } = useTutorial();
  const { t, i18n } = useTranslation();
  const [highlightPosition, setHighlightPosition] = useState(null);
  const overlayRef = useRef(null);

  // Define onboarding steps
  const steps = [
    {
      id: 'welcome',
      title: i18n.language === 'th' ? 'ยินดีต้อนรับสู่ Flow Agentic Sales!' : 'Welcome to Flow Agentic Sales!',
      content: i18n.language === 'th' 
        ? 'ระบบ CRM ที่ช่วยจัดการลูกค้า โอกาสทางการขาย และกิจกรรมต่างๆ ได้อย่างมีประสิทธิภาพ มาเริ่มทัวร์แนะนำการใช้งานกันเลย!'
        : 'A CRM system that helps manage customers, sales opportunities, and activities efficiently. Let\'s start the tour!',
      icon: Lightbulb,
      target: null,
      position: 'center'
    },
    {
      id: 'dashboard',
      title: i18n.language === 'th' ? 'แดชบอร์ด' : 'Dashboard',
      content: i18n.language === 'th'
        ? 'ภาพรวมของข้อมูลสำคัญ สถิติการขาย และกิจกรรมที่ต้องทำ ดูสรุปผลการทำงานได้ที่นี่'
        : 'Overview of important data, sales statistics, and activities. View your work summary here.',
      icon: ArrowUp,
      target: 'a[href="/dashboard"]',
      position: 'bottom'
    },
    {
      id: 'pipeline',
      title: i18n.language === 'th' ? 'Pipeline' : 'Pipeline',
      content: i18n.language === 'th'
        ? 'ดู Pipeline การขาย ติดตามความคืบหน้าของดีลทั้งหมด และจัดการขั้นตอนการขาย'
        : 'View sales pipeline, track all deal progress, and manage sales stages.',
      icon: ArrowUp,
      target: 'a[href="/pipeline"]',
      position: 'bottom'
    },
    {
      id: 'sales-assist',
      title: i18n.language === 'th' ? 'SalesAssist' : 'SalesAssist',
      content: i18n.language === 'th'
        ? 'เครื่องมือช่วยเหลือการขายด้วย AI วิเคราะห์ข้อมูล และให้คำแนะนำเพื่อเพิ่มโอกาสปิดการขาย'
        : 'AI-powered sales tools that analyze data and provide recommendations to increase closing rates.',
      icon: ArrowUp,
      target: 'a[href="/power-sales"]',
      position: 'bottom'
    },
    {
      id: 'sales-notes',
      title: i18n.language === 'th' ? 'บันทึก' : 'Notes',
      content: i18n.language === 'th'
        ? 'บันทึกการสนทนา ข้อมูลลูกค้า และรายละเอียดสำคัญจากการประชุมหรือการติดต่อ'
        : 'Record conversations, customer information, and important details from meetings or contacts.',
      icon: ArrowUp,
      target: 'a[href="/notes"]',
      position: 'bottom'
    },
    {
      id: 'customers',
      title: i18n.language === 'th' ? 'ลูกค้า' : 'Customers',
      content: i18n.language === 'th'
        ? 'จัดการข้อมูลลูกค้าทั้งหมด ดูประวัติการติดต่อ และติดตามความสัมพันธ์กับลูกค้า'
        : 'Manage all customer data, view contact history, and track customer relationships.',
      icon: ArrowUp,
      target: 'a[href="/customers"]',
      position: 'bottom'
    },
    {
      id: 'leads',
      title: i18n.language === 'th' ? 'ลีด' : 'Leads',
      content: i18n.language === 'th'
        ? 'จัดการลีดใหม่ ติดตามสถานะ และแปลงเป็นลูกค้าเมื่อพร้อม'
        : 'Manage new leads, track status, and convert to customers when ready.',
      icon: ArrowUp,
      target: 'a[href="/leads"]',
      position: 'bottom'
    },
    {
      id: 'opportunities',
      title: i18n.language === 'th' ? 'โอกาสทางการขาย' : 'Opportunities',
      content: i18n.language === 'th'
        ? 'ติดตามโอกาสทางการขาย คาดการณ์รายได้ และจัดการขั้นตอนการปิดการขาย'
        : 'Track sales opportunities, forecast revenue, and manage closing stages.',
      icon: ArrowUp,
      target: 'a[href="/opportunities"]',
      position: 'bottom'
    },
    {
      id: 'activities',
      title: i18n.language === 'th' ? 'กิจกรรม' : 'Activities',
      content: i18n.language === 'th'
        ? 'จัดการนัดหมาย งาน โทรศัพท์ อีเมล และกิจกรรมอื่นๆ ที่เกี่ยวข้องกับลูกค้า'
        : 'Manage appointments, tasks, calls, emails, and other customer-related activities.',
      icon: ArrowUp,
      target: 'a[href="/activities"]',
      position: 'bottom'
    },
    {
      id: 'knowledge-center',
      title: i18n.language === 'th' ? 'ศูนย์ความรู้' : 'Knowledge Center',
      content: i18n.language === 'th'
        ? 'เข้าถึงเอกสาร คู่มือ และข้อมูลสำคัญของบริษัท สำหรับช่วยในการทำงาน'
        : 'Access documents, guides, and important company information to help with work.',
      icon: ArrowUp,
      target: 'a[href="/knowledge-center"]',
      position: 'bottom'
    },
    {
      id: 'support-tickets',
      title: i18n.language === 'th' ? 'ตั๋วสนับสนุน' : 'Support Tickets',
      content: i18n.language === 'th'
        ? 'จัดการคำขอสนับสนุนจากลูกค้า ติดตามปัญหา และแก้ไขข้อร้องเรียน'
        : 'Manage customer support requests, track issues, and resolve complaints.',
      icon: ArrowUp,
      target: 'a[href="/support-tickets"]',
      position: 'bottom'
    },
    {
      id: 'help-button',
      title: i18n.language === 'th' ? 'ปุ่มช่วยเหลือ' : 'Help Button',
      content: i18n.language === 'th'
        ? 'คลิกปุ่มช่วยเหลือนี้ได้ทุกเมื่อเพื่อเริ่มทัวร์ใหม่ เปิด/ปิดคำแนะนำ หรือเข้าถึงคู่มือ'
        : 'Click this help button anytime to restart tour, toggle tooltips, or access documentation.',
      icon: ArrowUp,
      target: 'button[aria-label*="elp"], button[aria-label*="ช่วยเหลือ"]',
      position: 'bottom'
    },
    {
      id: 'profile',
      title: i18n.language === 'th' ? 'โปรไฟล์' : 'Profile',
      content: i18n.language === 'th'
        ? 'คลิกที่นี่เพื่อดูโปรไฟล์ของคุณ เปลี่ยนรหัสผ่าน หรือแก้ไขข้อมูลส่วนตัว'
        : 'Click here to view your profile, change password, or edit personal information.',
      icon: ArrowUp,
      target: 'a[href="/profile"]',
      position: 'bottom'
    },
    {
      id: 'complete',
      title: i18n.language === 'th' ? 'เรียบร้อย!' : 'All Set!',
      content: i18n.language === 'th'
        ? 'คุณพร้อมที่จะเริ่มใช้งานแล้ว! คลิกปุ่มช่วยเหลือได้ทุกเมื่อหากต้องการดูทัวร์นี้อีกครั้ง'
        : 'You\'re all set! Click the help button anytime if you want to see this tour again.',
      icon: Check,
      target: null,
      position: 'center'
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // Update highlight position when step changes
  useEffect(() => {
    if (!isOnboardingActive) return;

    const updatePosition = () => {
      // Remove highlight class from all elements
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });

      if (currentStepData.target) {
        const element = document.querySelector(currentStepData.target);
        if (element) {
          // Add highlight class to current element
          element.classList.add('tutorial-highlight');
          
          const rect = element.getBoundingClientRect();
          setHighlightPosition({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height,
            element: rect
          });
        } else {
          setHighlightPosition(null);
        }
      } else {
        setHighlightPosition(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      // Clean up highlight class when component unmounts
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [currentStep, isOnboardingActive, currentStepData.target]);

  // Scroll to element
  useEffect(() => {
    if (currentStepData.target && highlightPosition) {
      const element = document.querySelector(currentStepData.target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentStep, currentStepData.target, highlightPosition]);

  if (!isOnboardingActive) return null;

  const getTooltipPosition = () => {
    if (!highlightPosition || currentStepData.position === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const tooltipWidth = 400;
    const tooltipHeight = 200;
    const padding = 20;

    const positions = {
      top: {
        top: highlightPosition.element.top - tooltipHeight - padding,
        left: highlightPosition.element.left + (highlightPosition.element.width / 2) - (tooltipWidth / 2),
      },
      bottom: {
        top: highlightPosition.element.bottom + padding,
        left: highlightPosition.element.left + (highlightPosition.element.width / 2) - (tooltipWidth / 2),
      },
      left: {
        top: highlightPosition.element.top + (highlightPosition.element.height / 2) - (tooltipHeight / 2),
        left: highlightPosition.element.left - tooltipWidth - padding,
      },
      right: {
        top: highlightPosition.element.top + (highlightPosition.element.height / 2) - (tooltipHeight / 2),
        left: highlightPosition.element.right + padding,
      }
    };

    return positions[currentStepData.position] || positions.bottom;
  };

  const tooltipStyle = getTooltipPosition();
  const Icon = currentStepData.icon;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Overlay */}
      <div 
        ref={overlayRef}
        className="absolute inset-0 bg-black bg-opacity-30"
        onClick={skipOnboarding}
      />

      {/* Highlight */}
      {highlightPosition && (
        <>
          {/* Spotlight effect */}
          <div
            className="absolute bg-white bg-opacity-20 rounded-lg ring-4 ring-blue-400 ring-opacity-80 animate-pulse"
            style={{
              top: highlightPosition.top - 4,
              left: highlightPosition.left - 4,
              width: highlightPosition.width + 8,
              height: highlightPosition.height + 8,
            }}
          />
          {/* Clear area for the element */}
          <div
            className="absolute bg-transparent pointer-events-none"
            style={{
              top: highlightPosition.top,
              left: highlightPosition.left,
              width: highlightPosition.width,
              height: highlightPosition.height,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.3)',
              borderRadius: '8px'
            }}
          />
        </>
      )}

      {/* Tooltip */}
      <div
        className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 max-w-md"
        style={{
          ...tooltipStyle,
          zIndex: 10000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={skipOnboarding}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label={i18n.language === 'th' ? 'ข้าม' : 'Skip'}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Icon */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {currentStepData.title}
            </h3>
          </div>
        </div>

        {/* Content */}
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {currentStepData.content}
        </p>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
            <span>{i18n.language === 'th' ? 'ขั้นตอน' : 'Step'} {currentStep + 1} {i18n.language === 'th' ? 'จาก' : 'of'} {steps.length}</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={previousStep}
            disabled={currentStep === 0}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              currentStep === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{i18n.language === 'th' ? 'ก่อนหน้า' : 'Previous'}</span>
          </button>

          <button
            onClick={skipOnboarding}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-3 py-2 text-sm transition-colors"
          >
            {i18n.language === 'th' ? 'ข้าม' : 'Skip Tour'}
          </button>

          {isLastStep ? (
            <button
              onClick={completeOnboarding}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors"
            >
              <span>{i18n.language === 'th' ? 'เสร็จสิ้น' : 'Finish'}</span>
              <Check className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={nextStep}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              <span>{i18n.language === 'th' ? 'ถัดไป' : 'Next'}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;

